// Registry de usuarios/cotizaciones en Postgres por el hub-api.
// Workflows esperados:
//   POST /webhook/validate-contact { email, phone, name, company?, website? }
//     -> { ok, contact, reason?, message? }
//   Ya NO devuelve `userId` / `isReturning` / `priorQuotes`: en una ruta pública eso decía si un
//   correo cualquiera es cliente. Nadie los consumía. Ver `server/routes/cotizaciones.js`.
//   POST /webhook/save-quote { ...quotePayload }
//     -> { ok, quoteId, userId, createdAt, contact, totals }
//   POST /webhook/list-quotes { status?, search?, limit? }
//     -> { ok, count, quotes: [...] }
//   POST /webhook/update-quote { id, status?, notes?, historyEntry? }
//     -> { ok, quoteId, status, payload, history, trackingToken }
//   POST /webhook/quote-public { id, token }
//     -> { ok, quote: {...sanitized...} }
//   POST /webhook/send-quote-email { id?, email?, name?, pdfBase64?, trackingUrl?, customMessage? }
//     -> { ok, sent, to, quoteId }

import { hubPost, hubConfigurado } from './hub';
import { tokenParaBackend } from './adminAuth';

export const quotesConfigured = () => hubConfigurado();

export async function validateContactRemote({ email, phone, name, company, website } = {}) {
  if (!hubConfigurado()) return { ok: true, offline: true };
  return hubPost('validate-contact', { email, phone, name, company, website });
}

export async function saveQuoteRemote(payload) {
  if (!hubConfigurado()) return { ok: false, offline: true };
  // El cotizador ESPERA esta respuesta antes de dar la cotización por enviada (cupo por cliente):
  // 20 s y no los 45 por defecto, porque pasado eso se guarda local y se sigue igual.
  return hubPost('save-quote', payload, { timeoutMs: 20000 });
}

// Token de la sesión. `hubPost` solo manda `x-alebas-token` (el secreto compartido del servicio),
// que NO es la credencial del usuario — el guard del servidor lee `Authorization` o `body.token`,
// así que hay que ponerlo acá.
//
// 🔑 Y tiene que ser el de ADMINISTRACIÓN cuando lo hay: desde que `exigeAdmin` falla cerrado
// (2026-08-29) el `fluxai.jwt` es un `alg:none` y estas dos rutas lo rechazan con 401.
const sessionToken = () => tokenParaBackend();

// 🔴 ESTE ENDPOINT NUNCA FUNCIONÓ DESDE EL CLIENTE. Su guard (en el backend propio y en el
// workflow) exige un token, y esta llamada no mandaba ninguno: el comentario del workflow decía
// «BackOffice sends header» y era falso. Como `App.jsx` cierra el camino con `.catch(() => {})` y
// el panel cae a las cotizaciones del `localStorage`, el 401 llevaba meses siendo invisible: el
// admin creía estar viendo Postgres y veía su propio equipo.
export async function listQuotesRemote({ status, search, limit } = {}) {
  if (!hubConfigurado()) return { ok: false, offline: true, quotes: [] };
  return hubPost('list-quotes', { status, search, limit, token: sessionToken() });
}

export async function updateQuoteRemote({ id, status, notes, historyEntry } = {}) {
  if (!hubConfigurado()) return { ok: false, offline: true };
  return hubPost('update-quote', { id, status, notes, historyEntry, token: sessionToken() });
}

// "Mis cotizaciones" del usuario en sesión (por su correo) → CROSS-DEVICE. El cliente ve LAS
// SUYAS desde cualquier dispositivo, no las que quedaron en el localStorage del equipo.
export async function listMyQuotesRemote({ email } = {}) {
  if (!hubConfigurado() || !email) return { ok: false, offline: true, quotes: [] };
  return hubPost('list-my-quotes', { email, token: sessionToken() });
}

/**
 * Reconstruye una cotización con la FORMA del frontend a partir de una fila del backend.
 * El `payload` JSONB guarda el objeto completo que arma el cotizador (id/date/email/results/
 * budget/agpe/…); acá se le superpone lo AUTORITATIVO del servidor: id remoto estable (`r_<id>`)
 * y `status` (el admin lo cambia con update-quote). Robusto si `payload` viniera vacío.
 */
export function mapRemoteQuote(row) {
  if (!row) return null;
  const p = (row.payload && typeof row.payload === 'object') ? row.payload : {};
  return {
    ...p,
    id: 'r_' + row.id,
    status: row.status || p.status || 'nuevo',
    email: (row.email || p.email || '').toLowerCase(),
    name: p.name || row.name || row.email,
    date: p.date || (row.created_at ? new Date(row.created_at).toLocaleDateString('es-CO') : undefined),
    created_at: row.created_at || p.created_at,
    section_a_cop: row.section_a_cop ?? p.budget?.sA,
    section_b_cop: row.section_b_cop ?? p.budget?.sB,
    _remote: true,
  };
}

// Estados permitidos en el ciclo de vida de la cotización (sincronizado con update-quote).
export const QUOTE_STATUSES = ['nuevo', 'contactado', 'propuesta', 'negociacion', 'ganada', 'perdida', 'archivada'];

// Vista pública para el cliente (validada con token).
export async function getPublicQuote({ id, token } = {}) {
  if (!hubConfigurado()) return { ok: false, offline: true };
  return hubPost('quote-public', { id, token });
}

// Construye URL de seguimiento absoluta (la que se le envía al cliente por email).
export function buildTrackingUrl({ id, token, origin } = {}) {
  const base = origin || (typeof window !== 'undefined' ? window.location.origin : 'https://solar-hub.co');
  const remoteId = String(id || '').replace(/^r_/, '');
  return `${base}/?view=seguimiento&id=${remoteId}&t=${encodeURIComponent(token)}`;
}

// Envía email al cliente con resumen + tracking URL + PDF adjunto opcional.
// Backend: esquema/send-quote-email.json (Gmail SMTP via solarhub@alebas.co).
export async function sendQuoteEmail({ id, email, name, pdfBase64, trackingUrl, customMessage } = {}) {
  if (!hubConfigurado()) return { ok: false, offline: true };
  return hubPost('send-quote-email', { id, email, name, pdfBase64, trackingUrl, customMessage });
}
